import { Spin } from 'antd';
import { useEffect, useState } from 'react';

import { useLocation, useNavigate } from '@suntex.core/nav';
import { $$ } from '@suntex.core/text';
import { removePrefix } from '@suntex.core/text/transforms';
import { $GradeLevel, GradeLevel } from '@suntex.fim/enums';
import { CanvasHub, HubSettings, ModuleFilter } from '@suntex.fim/hub';

import { useNavigationContext } from '../NavigationContext';
import { usePlayerContext } from '../context';
import { useModuleAccess, usePlayerProgressSummary } from '../hooks';
import { useModuleNavigation } from '../navigation';
import { normalizeHubPath } from '../utils/modulePath';

import { ModuleCatalog } from './ModuleCatalog';

export interface HubProps {
  focus: ModuleFilter;
  gradeLevel: GradeLevel | undefined;
  settings: HubSettings;
}

export function Hub({ focus, gradeLevel, settings }: HubProps) {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { navigateToModule } = useModuleNavigation();
  // Remove leading # from hash to normalize the path value
  const normalizedHash = normalizeHubPath($$(hash, removePrefix('#')));
  const [path, setPath] = useState(
    normalizedHash || settings.defaultPath || '/',
  );
  const { isModuleEnabled, enabledModules, moduleTagging, isLoading } =
    useModuleAccess();
  const { isPreview } = usePlayerContext();
  const { previewModulesPath } = useNavigationContext();
  const progressSummary = usePlayerProgressSummary();

  // Sync path state to URL hash when path changes
  useEffect(() => {
    const hashPath = $$(hash, removePrefix('#'));
    if (hashPath !== path) {
      navigate(`#${path}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, navigate]);

  // Sync URL hash to path state when hash changes externally
  useEffect(() => {
    const hashPath = normalizeHubPath($$(hash, removePrefix('#')));
    if (hashPath && hashPath !== path) {
      setPath(hashPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);

  useEffect(() => {
    if (gradeLevel) {
      const newPath = `/stages-of-learning/${$GradeLevel.describe(gradeLevel).slug}`;
      if (
        path.startsWith('/stages-of-learning/') &&
        !path.startsWith(newPath)
      ) {
        setPath(newPath);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradeLevel]);

  if (settings.catalogView) {
    if (isLoading || !moduleTagging || !enabledModules) return <Spin />;
    return (
      <ModuleCatalog
        modules={enabledModules}
        path={path}
        moduleTagging={moduleTagging}
      />
    );
  } else {
    if (isLoading || !moduleTagging || !enabledModules) return <Spin />;
    return (
      <>
        <span style={{ display: 'none' }}>
          {JSON.stringify({ hash, path })}
        </span>
        <CanvasHub
          config={{
            preview: isPreview,
            previewModulesPath,
          }}
          {...{
            // deliberately bypassing react-tracked hash
            path,
            isModuleEnabled,
            focus,
            gradeLevel,
            progressSummary,
            settings,
            moduleTagging,
            setPath,
            navigate: navigateToModule,
          }}
        />
      </>
    );
  }
}
